const PROXY_ENDPOINT = '/apps/b2b-approval/register';

const selectors = {
  form: '[data-customer-approval-form]',
  vatInput: '[data-vat-input]',
  vatLabel: '[data-vat-label]',
  vatFeedback: '[data-vat-feedback]',
  formStatus: '[data-form-status]',
  submitButton: '[data-submit-button]'
};

const regionConfig = {
  uk: {
    fieldLabel: 'VAT Number',
    placeholder: 'Eg: GB123456789',
    pattern: /^GB\d{9}(\d{3})?$/i,
    formatError: 'Enter a valid UK VAT number, eg GB123456789.'
  },
  eu: {
    fieldLabel: 'VAT Number',
    placeholder: 'Eg: IE1234567X',
    pattern: /^[A-Z]{2}[A-Z0-9]{2,12}$/i,
    formatError: 'Enter a valid EU VAT number, starting with the country code, eg IE1234567X.'
  },
  usa: {
    fieldLabel: 'EIN (Employer Identification Number)',
    placeholder: 'Eg: 12-3456789',
    pattern: /^\d{2}-?\d{7}$/,
    formatError: 'Enter a valid EIN, eg 12-3456789.'
  }
};

class CustomerApprovalForm {
  constructor(form) {
    this.form = form;
    this.region = regionConfig[form.dataset.region] ? form.dataset.region : 'uk';
    this.config = regionConfig[this.region];

    this.elements = {
      vatInput: form.querySelector(selectors.vatInput),
      vatLabel: form.querySelector(selectors.vatLabel),
      vatFeedback: form.querySelector(selectors.vatFeedback),
      formStatus: form.querySelector(selectors.formStatus),
      submitButton: form.querySelector(selectors.submitButton)
    };

    this._applyRegionCopy();
    this._setupEventListeners();
  }

  _applyRegionCopy() {
    if (this.elements.vatLabel) {
      const requiredMark = this.elements.vatLabel.querySelector('.reqd');
      this.elements.vatLabel.textContent = this.config.fieldLabel + ' ';
      if (requiredMark) this.elements.vatLabel.appendChild(requiredMark);
    }
    if (this.elements.vatInput) {
      this.elements.vatInput.placeholder = this.config.placeholder;
    }
  }

  _setupEventListeners() {
    if (this.elements.vatInput) {
      this.elements.vatInput.addEventListener('blur', this._handleVatBlur);
    }
    this.form.addEventListener('submit', this._handleSubmit);
  }

  _handleVatBlur = () => {
    const value = this.elements.vatInput.value.trim();
    if (!value) return;
    this._validateVatFormat(value);
  }

  _validateVatFormat(value) {
    const isValid = this.config.pattern.test(value);
    this.elements.vatInput.setAttribute('aria-invalid', (!isValid).toString());
    if (this.elements.vatFeedback) {
      this.elements.vatFeedback.textContent = isValid ? '' : this.config.formatError;
      this.elements.vatFeedback.setAttribute('aria-invalid', (!isValid).toString());
    }
    return isValid;
  }

  _setStatus(message, isError) {
    if (!this.elements.formStatus) return;
    this.elements.formStatus.textContent = message;
    this.elements.formStatus.setAttribute('aria-invalid', (!!isError).toString());
  }

  _setFieldError(name, message) {
    const field = this.form.querySelector(`[name="${name}"]`);
    if (field) field.setAttribute('aria-invalid', 'true');
    if (name === 'vat_number' && this.elements.vatFeedback) {
      this.elements.vatFeedback.textContent = message;
      this.elements.vatFeedback.setAttribute('aria-invalid', 'true');
      return;
    }
    this._setStatus(message, true);
  }

  _clearFieldErrors() {
    this.form.querySelectorAll('[aria-invalid]').forEach((el) => el.removeAttribute('aria-invalid'));
    if (this.elements.vatFeedback) this.elements.vatFeedback.textContent = '';
  }

  _buildPayload() {
    const formData = new FormData(this.form);
    return {
      region: this.region,
      first_name: formData.get('first_name'),
      last_name: formData.get('last_name'),
      business_name: formData.get('business_name'),
      vat_number: (formData.get('vat_number') || '').trim(),
      nature_of_business: formData.get('nature_of_business') || '',
      email: formData.get('email'),
      hear_about: formData.get('hear_about') || '',
      accepts_marketing: formData.get('accepts_marketing') === 'on',
      terms: formData.get('terms') === 'on'
    };
  }

  _handleSubmit = async (event) => {
    event.preventDefault();
    this._clearFieldErrors();

    if (!this.form.checkValidity()) {
      this._setStatus('Please fill in all required fields.', true);
      return;
    }

    const vatValue = this.elements.vatInput.value.trim();
    if (!this._validateVatFormat(vatValue)) {
      return;
    }

    this.elements.submitButton.disabled = true;
    this._setStatus('Checking your details …', false);

    try {
      const response = await fetch(PROXY_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(this._buildPayload())
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        this._handleFailure(result);
        return;
      }

      this._handleSuccess(result);
    } catch (error) {
      this._setStatus('Something went wrong submitting your application. Please try again.', true);
    } finally {
      this.elements.submitButton.disabled = false;
    }
  }

  _handleFailure(result) {
    if (result && result.errors) {
      Object.keys(result.errors).forEach((name) => this._setFieldError(name, result.errors[name]));
      return;
    }
    this._setStatus((result && result.message) || 'We could not process your application. Please check your details and try again.', true);
  }

  _handleSuccess(result) {
    this.form.reset();
    this.elements.submitButton.disabled = true;
    this._setStatus(
      result.message ||
        (result.approved
          ? "Thanks — your trade account is approved. You can sign in anytime with a one-time code sent to your email."
          : "Thanks for applying. We'll verify your details and be in touch shortly."),
      false
    );
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll(selectors.form).forEach((form) => new CustomerApprovalForm(form));
});
